import React, { useState, useEffect } from "react";
import { Divider } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { Tab, Tabs } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import LibraryTable from "../../components/CommentLibrary/LibraryTable";
import CLSummary from "../../components/CommentLibrary/CommentLibraryTags/CLSummary";
import CLTags from "../../components/CommentLibrary/CommentLibraryTags/CLTags";
import { apiCall } from "../../utils/action";
import ImportProgress from "../../components/CommentLibrary/ImportProgress";
import CLRating from "../../components/CommentLibrary/CommentLibraryTags/CLRatings";
import GlobalList from "../../components/CommentLibrary/CommentLibraryTags/GlobalList";
import { setIsHTCompany, globalListEnable, GetTemplateList } from "../../store/action";
import { useSearchParams } from "react-router-dom";

const StyledTabs = styled((props) => <Tabs {...props} TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }} />)({
  "& .MuiTabs-indicator": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicatorSpan": {
    maxWidth: 75,
    borderRadius: 5,
    width: "100%",
    height: "30px !important",
    backgroundColor: "#0062A0",
  },
});

const StyledTab = styled((props) => (
  <Tab
    style={{
      width: props?.width,
      textTransform: "none",
      fontWeight: "500",
      fontFamily: '"Source Sans Pro", sans-serif !important',
      fontStyle: "normal",
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0.1px",
      marginRight: "8px",
      color: "#42474E",
    }}
    disableRipple
    {...props}
  ></Tab>
))(({ theme }) => ({
  textTransform: "none",
  fontWeight: "500 !important",
  fontFamily: '"Source Sans Pro", sans-serif !important',
  fontStyle: "normal !important",
  fontSize: theme.typography.pxToRem(14),
  lineHeight: "20px !important",
  letterSpacing: "0.1px !important",
  marginRight: theme.spacing(1),
  color: "#42474E !important",
  "&.Mui-selected": {
    color: "#0062A0 !important",
  },
  "&.Mui-focusVisible": {
    backgroundColor: "#0062A0 !important",
  },
}));
const STATUS_MAP = {
  all: undefined,       // don't send status for "All"
  used: 'used',
  unused: 'unused',
  summary: 'summary',
  deleted: 'deleted',
};
/**
 * LibraryTabs component
 *
 * Renders library tabs and passes the selected tab value to the LibraryTable component
 */
function LibraryTabs() {
  /**
   * Tracks the selected tab value
   */
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [searchParams] = useSearchParams();
  const matches = useMediaQuery("(min-width:900px)");
  const globalListStoreData = useSelector((state) => state.globalList);
  const storeData = useSelector((state) => state.commentLibrary);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importErrors, setImportErrors] = useState([]);
  const [state, setState] = useState({
    page: 0,
    rowsPerPage: parseInt(sessionStorage.getItem("commentsRowPerPage"),10) || 10,
    order: "",     // default
    orderBy: "",
    search: false,
    selected: [],
    expandedRows: [],
    commentListData: null,
    commentListInitialData: null,
    commentTitle: "",
    commentTag: [],
    commentSummaryTag: [],
    commentText: { innerHTML: "", dropodownList: [] },
    commentSummary: { innerHTML: "", dropdownList: [] },
    showLoader: false,
    searchText: "",
    filterTagList: storeData?.clTagList,
    selectedFilterTagID: [],
    totalRowCount: -1,
    filterType: "all",
    listLoaded: false,
    globalSearchTableData: [],
    openAddDialog: false,
    commentDropdownList: [],
    summaryDropdownList: [],
    isEdit: false,
    refreshTs: null,
  });

  const isGlobalListEnable = globalListStoreData?.globalListEnable;
  const isHTCompany = storeData?.isHTCompany;
  const isHTResolved = isHTCompany !== undefined;

  // Dynamic tab indices based on isGlobalListEnable
  const TAB_INDICES = {
    comments: 0,
    lists: isGlobalListEnable === 1 ? 1 : -1,
    tags: isGlobalListEnable === 1 ? 2 : 1,
    summaries: isGlobalListEnable === 1 ? 3 : 2,
  };

  useEffect(() => {
    if (!isHTResolved) return;
    if (searchParams.get("tab") === "ratings" && !isHTCompany) {
      setTabValue(TAB_INDICES.summaries);
    }
  }, [searchParams, isHTCompany, isHTResolved, isGlobalListEnable]);

  /**
   * Handles tab changes
   * @param {object} event - the event object
   * @param {number} newValue - the index of the selected tab
   */
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch((state.searchText || "").trim());
    }, 500); // debounce delay
    return () => clearTimeout(t);
  }, [state.searchText]);
  const GetCommentList = (formData, type) => {
    /**
     * Fetch comment list data from API
     * @param {number} pageNumber current page number
     */
    if (!isHTCompany) return;
    setState((prevState) => ({ ...prevState, showLoader: true }));
    apiCall(
      (response) => {
        const { data, message, success, tagsList } = response?.data;
        if (success) {
          setState((prevState) => ({ ...prevState, commentListData: data?.data, totalRowCount: data?.total || 0, search: false, listLoaded: true, commentListInitialData: data?.data, showLoader: false }));
          if (type !== "filter") {
            setState((prevState) => ({ ...prevState, filterTagList: tagsList }));
          }
        } else {
          console.log("else", message);
        }
      },
      formData,
      "GET_COMMENT_LIBRARY_LIST"
    );
  };
  const handleTabChange = (event, newTabValue) => {
    // if (newTabValue === 2 && !storeData?.isHTCompany) return;
    setTabValue(newTabValue);
    if (newTabValue === TAB_INDICES.comments) {
      GetCommentList({
        page: (state.page || 0) + 1,
        rows_per_page: state.rowsPerPage || parseInt(sessionStorage.getItem("commentsRowPerPage"), 10) || 10,
        ...(debouncedSearch && { searchText: debouncedSearch }),
        filterByType: state.filterType,
        filterByTagId: state.selectedFilterTagID,
        sort_field: state.orderBy,
        sort_direction: state.order,
      });
    }
  };

  // The template list page is the other place that resolves is_ht_company; landing
  // straight on the library (deep link, refresh in a new tab) has to resolve it here
  // or every consumer below stays stuck on undefined.
  useEffect(() => {
    if (isHTResolved || !sessionStorage.getItem("company-key")) return;
    let cancelled = false;
    (async () => {
      const formdata = { company_id: 1, user_id: 2, role_id: 2 };
      const response = await dispatch(GetTemplateList(formdata));
      if (cancelled || !response?.data?.success) return;
      dispatch(setIsHTCompany(response?.data?.is_ht_company));
      const listFlag = Number(response?.data?.is_global_list_enable ?? 0);
      sessionStorage.setItem("global-list-enable", String(listFlag));
      dispatch(globalListEnable(listFlag));
    })();
    return () => { cancelled = true; };
  }, [isHTResolved, dispatch]);

  useEffect(() => {
    if (!isHTCompany) return;
    GetCommentList({
      page: state.page + 1,
      rows_per_page: state.rowsPerPage,
      ...(STATUS_MAP[state.filterType] && { status: STATUS_MAP[state.filterType] }),
      ...(debouncedSearch && { searchText: debouncedSearch }),
      filterByType: state.filterType,
      filterByTagId: state.selectedFilterTagID,
      sort_field: state.orderBy,
      sort_direction: state.order
    });
  }, [
    isHTCompany,
    state.page,
    state.rowsPerPage,
    state.filterType,
    state.selectedFilterTagID,
    debouncedSearch,
    importing,
    state.refreshTs,
    state.order,
    state.orderBy
  ]);

  return (
    <div className="library-tabs">
      {importing && (
        <ImportProgress
          progress={progress}
          label={tabValue === TAB_INDICES.lists ? "Importing lists" : "Importing comments"}
        />
      )}
      <StyledTabs value={tabValue} className="styled-tab" onChange={handleTabChange} aria-label="styled tabs example">
        <StyledTab label="Comments" width={matches ? "215px" : ""} />
        {isGlobalListEnable === 1 && <StyledTab label="Lists" width={matches ? "215px" : ""} />}
        {/* <StyledTab label="Media" width={matches ? "215px" : ""} /> */}
        <StyledTab label="Tags" width={matches ? "215px" : ""} />
        <StyledTab label={isHTCompany ? "Summaries" : "Ratings"} width={matches ? "215px" : ""} />
      </StyledTabs>
      <Divider sx={{ border: "0px 0px 1px 0px !important", color: "#D5D5D5" }} />
      {tabValue === TAB_INDICES.comments && <LibraryTable importing={importing} setImportErrors={setImportErrors} setImporting={setImporting} progress={progress} setProgress={setProgress} setState={setState} state={state} GetCommentList={GetCommentList} />}
      {tabValue === TAB_INDICES.lists && isGlobalListEnable === 1 && <GlobalList state={state} setState={setState} importing={importing} setImportErrors={setImportErrors} setImporting={setImporting} progress={progress} setProgress={setProgress} />}
      {tabValue === TAB_INDICES.tags && isHTResolved && <CLTags isHTCompany={isHTCompany} />}
      {tabValue === TAB_INDICES.summaries && isHTResolved && (isHTCompany ? <CLSummary /> : <CLRating isHTCompany={isHTCompany} />)}
    </div>
  );
}

export default LibraryTabs;
